import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Backdrop,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  NavigateBefore as BackIcon,
  NavigateNext as NextIcon,
  CheckCircle as SubmitIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  CameraAlt as CameraIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { chalanAPI, vehicleFeeAPI } from '../services/chalanService';
import PageLoader from '../components/PageLoader';
import { hasFeature } from '../utils/permissions';

// Default wizard steps definition with keys. The display label is looked up
// via `newPermit.step_<key>` at render time since this array lives outside
// the component and can't call the translation hook directly.
const DEFAULT_WIZARD_STEPS = [
  { key: 'permit_configuration', order: 1 },
  { key: 'vehicle_details', order: 2 },
  { key: 'owner_information', order: 3 },
  { key: 'additional_info', order: 4 },
  { key: 'create_chalan', order: 5 },
  { key: 'documents_submission', order: 6 },
  { key: 'review_submit', order: 7 },
];

function NewPermit() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const orgContext = useOrg();
  // Get authority from org context - use organization_type if available, fallback to orgCode
  const currentAuthority = orgContext?.org?.organization_type || orgContext?.orgCode || localStorage.getItem('orgCode') || 'RTA';

  // Wizard configuration state
  const [wizardConfig, setWizardConfig] = useState(null);
  const [wizardConfigLoading, setWizardConfigLoading] = useState(true);

  // Compute enabled steps based on wizard config
  const enabledSteps = React.useMemo(() => {
    if (!wizardConfig) {
      // Return all steps if no config loaded yet
      return DEFAULT_WIZARD_STEPS.map(s => ({ ...s, label: t(`newPermit.step_${s.key}`), enabled: true }));
    }

    return DEFAULT_WIZARD_STEPS
      .map(step => {
        const config = wizardConfig[step.key];
        let enabled = config?.enabled !== false; // default to true if not specified

        // The "Create Chalan" step lets the user issue a chalan while
        // creating a permit — restrict it to users who actually have
        // chalan-create access, same as the standalone Chalan creation flow.
        if (step.key === 'create_chalan' && !hasFeature(user, 'chalan_create')) {
          enabled = false;
        }

        return {
          ...step,
          label: config?.label || t(`newPermit.step_${step.key}`),
          enabled,
        };
      })
      .filter(step => step.enabled);
  }, [wizardConfig, user, t]);

  // Step labels for the Stepper
  const steps = enabledSteps.map(s => s.label);

  // Map visual step index to actual step key
  const getStepKey = (visualIndex) => {
    return enabledSteps[visualIndex]?.key || null;
  };

  // Map step key to visual step index
  const getVisualStepIndex = (stepKey) => {
    const index = enabledSteps.findIndex(s => s.key === stepKey);
    return index >= 0 ? index : 0;
  };

  // Check if user is an employee or admin
  const isEmployeeOrAdmin = user && (
    user?.role?.name === 'admin' ||
    user?.role === 'admin' ||
    user?.is_staff === true ||
    (user?.features && user.features.some(f =>
      (f.name?.toLowerCase() === 'employee' || f.toLowerCase?.() === 'employee')
    ))
  );

  // Check if user is end_user or vehicle_owner (auto-fill owner info from user)
  const isOwnerRole = user && (
    user?.role?.name === 'end_user' ||
    user?.role?.name === 'enduser' ||
    user?.role === 'end_user' ||
    user?.role === 'enduser' ||
    user?.role?.name === 'vehicle_owner' ||
    user?.role === 'vehicle_owner' ||
    (user?.features && user.features.some(f => {
      const featureName = (f.name || f || '').toLowerCase().replace(/[_\s]/g, '');
      return featureName === 'enduser' || featureName === 'vehicleowner';
    }))
  );

  const [activeStep, setActiveStep] = useState(0);
  const [viewMode, setViewMode] = useState('drafts');
  // Compute initial valid authority: must be 'PTA' or 'RTA', default 'RTA'
  const initialAuthority = (() => {
    const stored = localStorage.getItem('orgCode');
    return ['PTA', 'RTA'].includes(stored) ? stored : 'RTA';
  })();

  const [formData, setFormData] = useState({
    authority: initialAuthority,
    permit_type_id: '',
    vehicle_number: '',
    vehicle_type_id: '',
    vehicle_make: '',
    vehicle_model: '',
    vehicle_year: new Date().getFullYear(),
    vehicle_capacity: '',
    owner_name: '',
    owner_cnic: '',
    owner_email: '',
    owner_phone: '',
    owner_address: '',
    description: '',
    remarks: '',
    restrictions: '',
    approved_routes: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: '',
  });
  const [permitTypes, setPermitTypes] = useState([]);
  const [routesList, setRoutesList] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [createdPermitId, setCreatedPermitId] = useState(null);
  const [createdChalanId, setCreatedChalanId] = useState(null);
  const [renewalPermitId, setRenewalPermitId] = useState(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);

  // Chalan form states
  const [chalanData, setChalanData] = useState({
    owner_name: '',
    owner_cnic: '',
    owner_phone: '',
    car_number: '',
    permit: '',
    vehicle_type: '',
    violation_description: '',
    fees_amount: '',
    remarks: '',
  });
  const [chalanAttachment, setChalanAttachment] = useState(null);
  const [autoCalcFee, setAutoCalcFee] = useState(true);
  const [userDrafts, setUserDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [deletingDraft, setDeletingDraft] = useState(false);
  // Fee structure fetched from the backend (from Chalans => Manage vehicle fees)
  const [vehicleFee, setVehicleFee] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);

  // Fetch wizard configuration on mount
  // Track if the init effect has already run so it never fires twice.
  const initDoneRef = useRef(false);
  const csvInputRef = useRef(null);

  // Single init effect — fires once when user is available.
  // All lookup APIs (wizard-config, permit-types, vehicle-types, routes) are
  // fetched in parallel with Promise.all so the page renders as soon as all
  // dropdown data arrives, instead of waiting for 4 sequential round-trips.
  useEffect(() => {
    if (!user || initDoneRef.current) return;
    initDoneRef.current = true;

    // Parallel fetch of all static lookups + wizard config
    Promise.all([
      apiClient.get('/wizard-config/').catch(() => null),
      apiClient.get('/permit-types/').catch(() => null),
      apiClient.get('/vehicle-types/').catch(() => null),
      apiClient.get('/routes/').catch(() => null),
      apiClient.get('/document-types/').catch(() => null),
    ]).then(([wizardRes, typesRes, vehicleRes, routesRes, docTypesRes]) => {
      if (wizardRes) setWizardConfig(wizardRes.data.wizard_steps || null);
      setWizardConfigLoading(false);
      if (typesRes) setPermitTypes(typesRes.data.results || typesRes.data || []);
      if (vehicleRes) setVehicleTypes(vehicleRes.data.results || vehicleRes.data || []);
      if (routesRes) setRoutesList(routesRes.data.results || routesRes.data || []);
      if (docTypesRes) setDocumentTypes((docTypesRes.data.results || docTypesRes.data || []).filter(t => t.is_active));
    }).catch(() => {
      setWizardConfigLoading(false);
    });

    // Pre-fill owner information from logged-in user ONLY if the user has
    // an end_user or vehicle_owner role. For all other roles (admin, employee,
    // clerks, etc.), leave owner_name and owner_email empty so the staff member
    // can manually enter the actual permit/applicant owner details.
    if (isOwnerRole) {
      setFormData(prevData => ({
        ...prevData,
        owner_name: user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.username || '',
        owner_email: user.email || '',
      }));
      setChalanData(prevData => ({
        ...prevData,
        owner_name: user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.username || '',
      }));
    } else {
      // Ensure owner_name and owner_email are empty for non-owner roles
      setFormData(prevData => ({
        ...prevData,
        owner_name: '',
        owner_email: '',
      }));
      setChalanData(prevData => ({
        ...prevData,
        owner_name: '',
      }));
    }

    // Check for renewal permit parameter (read URL once, never put in deps)
    const params = new URLSearchParams(window.location.search);
    const renewalId = params.get('renewalPermitId');
    const validFrom = params.get('validFrom');
    const validTo = params.get('validTo');

    if (renewalId) {
      setRenewalPermitId(renewalId);
      setViewMode('new');
      fetchExpiredPermitAndPrefill(renewalId, validFrom, validTo);
    } else {
      // Only load drafts when not in a renewal flow
      fetchUserDrafts();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update authority when org context changes
  useEffect(() => {
    if (orgContext?.org) {
      const authority = orgContext.org.organization_type || orgContext.orgCode || 'RTA';
      // Only set to PTA or RTA (valid authority choices)
      const validAuthority = ['PTA', 'RTA'].includes(authority) ? authority : 'RTA';
      setFormData(prev => ({
        ...prev,
        authority: validAuthority,
      }));
    }
  }, [orgContext?.org, orgContext?.orgCode]);

  const fetchExpiredPermitAndPrefill = async (expiredPermitId, validFrom, validTo) => {
    try {
      const response = await apiClient.get(`/permits/${expiredPermitId}/`);
      const expiredPermit = response.data;

      // Pre-fill all fields from the expired permit
      const prefillData = {
        authority: expiredPermit.authority || currentAuthority,
        permit_type_id: expiredPermit.permit_type_id || '',
        vehicle_number: expiredPermit.vehicle_number || '',
        vehicle_type_id: expiredPermit.vehicle_type_id || '',
        vehicle_make: expiredPermit.vehicle_make || '',
        vehicle_model: expiredPermit.vehicle_model || '',
        vehicle_year: expiredPermit.vehicle_year || new Date().getFullYear(),
        vehicle_capacity: expiredPermit.vehicle_capacity || '',
        owner_name: expiredPermit.owner_name || '',
        owner_cnic: expiredPermit.owner_cnic || '',
        owner_email: expiredPermit.owner_email || '',
        owner_phone: expiredPermit.owner_phone || '',
        owner_address: expiredPermit.owner_address || '',
        description: expiredPermit.description || '',
        remarks: expiredPermit.remarks || '',
        restrictions: expiredPermit.restrictions || '',
        approved_routes: expiredPermit.approved_routes || '',
        valid_from: validFrom || new Date().toISOString().split('T')[0],
        valid_to: validTo || '',
      };

      setFormData(prefillData);

      // Also pre-fill chalan data
      setChalanData({
        owner_name: expiredPermit.owner_name || '',
        owner_cnic: expiredPermit.owner_cnic || '',
        owner_phone: expiredPermit.owner_phone || '',
        car_number: expiredPermit.vehicle_number || '',
        permit: '',
        vehicle_type: expiredPermit.vehicle_type_id || '',
        violation_description: 'Permit Renewal Fee',
        fees_amount: '',
        remarks: `Renewal for permit: ${expiredPermit.permit_number}`,
      });

      // Auto-jump to Create Chalan step
      const chalanStepIndex = getVisualStepIndex('create_chalan');
      setActiveStep(chalanStepIndex);

      setMessage({
        type: 'success',
        text: t('newPermit.renewalLoadedMessage', { number: expiredPermit.permit_number }),
      });
    } catch (err) {
      console.error('Failed to fetch expired permit:', err);
      setMessage({
        type: 'error',
        text: t('newPermit.renewalLoadFailed'),
      });
    }
  };

  const fetchUserDrafts = async () => {
    try {
      setLoadingDrafts(true);
      const response = await apiClient.get('/permits/my_drafts/');
      setUserDrafts(response.data.drafts || []);
    } catch (err) {
      console.error('Failed to fetch user drafts:', err);
      // Don't show error, just silently fail on draft fetch
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleConfirmDeleteDraft = async () => {
    if (!draftToDelete) return;
    try {
      setDeletingDraft(true);
      await apiClient.delete(`/permits/${draftToDelete.id}/delete_draft/`);
      setUserDrafts(prev => prev.filter(d => d.id !== draftToDelete.id));
      if (currentDraftId === draftToDelete.id) {
        setCurrentDraftId(null);
      }
      setMessage({ type: 'success', text: t('newPermit.draftDeleted') });
    } catch (err) {
      console.error('Failed to delete draft:', err);
      setMessage({ type: 'error', text: t('newPermit.draftDeleteFailed') });
    } finally {
      setDeletingDraft(false);
      setDraftToDelete(null);
    }
  };

  const handleLoadDraft = (draft) => {
    try {
      // Pre-fill form with draft data
      setFormData({
        authority: draft.authority || currentAuthority,
        permit_type_id: draft.permit_type_id || '',
        vehicle_number: draft.vehicle_number || '',
        vehicle_type_id: draft.vehicle_type_id || '',
        vehicle_make: draft.vehicle_make || '',
        vehicle_model: draft.vehicle_model || '',
        vehicle_year: draft.vehicle_year || new Date().getFullYear(),
        vehicle_capacity: draft.vehicle_capacity || '',
        owner_name: draft.owner_name || '',
        owner_cnic: draft.owner_cnic || '',
        owner_email: draft.owner_email || '',
        owner_phone: draft.owner_phone || '',
        owner_address: draft.owner_address || '',
        description: draft.description || '',
        remarks: draft.remarks || '',
        restrictions: draft.restrictions || '',
        approved_routes: draft.approved_routes || '',
        valid_from: draft.valid_from || new Date().toISOString().split('T')[0],
        valid_to: draft.valid_to || '',
      });

      setCurrentDraftId(draft.id);

      // Restore the wizard step the user was on when they saved the draft.
      // Clamp to valid range so a stale value from a config change can't break the stepper.
      const savedStep = typeof draft.wizard_step === 'number' ? draft.wizard_step : 0;
      // We can only clamp once enabledSteps are computed, so defer — but steps is available here
      // because enabledSteps is a memo computed before this handler runs.
      const clampedStep = Math.min(Math.max(0, savedStep), Math.max(0, enabledSteps.length - 1));
      setActiveStep(clampedStep);

      setViewMode('new');
      setMessage({
        type: 'success',
        text: t('newPermit.draftLoadedMessage', { step: clampedStep + 1, label: enabledSteps[clampedStep]?.label || t('permitDetails.stepNumber', { number: clampedStep + 1 }) }),
      });
    } catch (err) {
      console.error('Error loading draft:', err);
      setMessage({
        type: 'error',
        text: t('newPermit.draftLoadFailed'),
      });
    }
  };

  // Simple email format validation
  const isValidEmail = (email) => {
    if (!email || email.trim() === '') return true; // optional field
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  };

  // Check if email field has an invalid value entered (non-empty but invalid format)
  const isEmailInvalid = formData.owner_email && !isValidEmail(formData.owner_email);

  // Helper to extract clean error messages from backend validation errors
  const extractErrorMessage = (err) => {
    const data = err.response?.data;
    if (!data) return err.message || t('newPermit.createPermitFailedGeneric');

    // Check for detail/message first
    if (typeof data.detail === 'string') return data.detail;
    if (typeof data.message === 'string') return data.message;
    if (typeof data === 'string') return data;

    // Field-level validation errors: extract first message from each field
    if (typeof data === 'object') {
      const fieldMessages = [];
      for (const [field, errors] of Object.entries(data)) {
        if (Array.isArray(errors)) {
          const cleanMsg = errors
            .map(e => (typeof e === 'string' ? e : e?.string || e?.message || String(e)))
            .join('. ');
          if (cleanMsg) {
            fieldMessages.push(`${field}: ${cleanMsg}`);
          }
        } else if (typeof errors === 'string') {
          fieldMessages.push(`${field}: ${errors}`);
        }
      }
      if (fieldMessages.length > 0) {
        return fieldMessages.join(' | ');
      }
    }

    // Fallback
    try {
      return JSON.stringify(data);
    } catch {
      return t('newPermit.createPermitFailedGeneric');
    }
  };

  // Validate current step based on step key
  const validateStep = (stepIndex) => {
    const stepKey = getStepKey(stepIndex);
    switch (stepKey) {
      case 'permit_configuration':
        return formData.permit_type_id !== '';
      case 'vehicle_details':
        return (
          formData.vehicle_number !== '' &&
          formData.vehicle_type_id !== '' &&
          formData.vehicle_make !== '' &&
          formData.vehicle_model !== '' &&
          formData.vehicle_year !== '' &&
          formData.vehicle_capacity !== ''
        );
      case 'owner_information': {
        // Check required fields are filled
        const requiredFilled =
          formData.owner_name !== '' &&
          formData.owner_cnic !== '' &&
          formData.owner_phone !== '' &&
          formData.owner_address !== '';
        if (!requiredFilled) return false;
        // Validate email format if provided
        if (formData.owner_email && !isValidEmail(formData.owner_email)) return false;
        return true;
      }
      case 'additional_info':
        return formData.description !== '';
      case 'create_chalan':
        return (
          (chalanData.owner_name || formData.owner_name).trim() !== '' &&
          (chalanData.owner_cnic || formData.owner_cnic).trim() !== '' &&
          (chalanData.car_number || formData.vehicle_number).trim() !== '' &&
          (chalanData.vehicle_type || formData.vehicle_type_id) !== '' &&
          chalanData.violation_description.trim() !== '' &&
          !!chalanAttachment
        );
      case 'documents_submission': {
        // Block "Next" until all compulsory document types have a file uploaded
        const normalise = (val) => {
          if (!Array.isArray(val)) return [];
          return val.map(item => typeof item === 'string' ? { key: item, required: true } : item);
        };
        const selectedPermitType = permitTypes.find(t => t.id === parseInt(formData.permit_type_id, 10));
        const docTypeEntries = selectedPermitType?.required_document_types?.length
          ? normalise(selectedPermitType.required_document_types)
          : documentTypes.map(dt => ({ key: dt.key, required: dt.is_required !== false }));
        const compulsoryKeys = docTypeEntries.filter(e => e.required).map(e => e.key);
        return compulsoryKeys.every(key => pendingDocuments.some(d => d.document_type === key));
      }
      case 'review_submit':
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    } else {
      const stepKey = getStepKey(activeStep);
      let errorText = t('newPermit.fillRequiredFields');
      if (stepKey === 'owner_information' && formData.owner_email && !isValidEmail(formData.owner_email)) {
        errorText = t('newPermit.enterValidEmail');
      }
      setMessage({
        type: 'error',
        text: errorText,
      });
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Format CNIC: 12345-1234567-1
    if (name === 'owner_cnic') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length > 0) {
        if (cleaned.length <= 5) {
          processedValue = cleaned;
        } else if (cleaned.length <= 12) {
          processedValue = cleaned.slice(0, 5) + '-' + cleaned.slice(5);
        } else {
          processedValue = cleaned.slice(0, 5) + '-' + cleaned.slice(5, 12) + '-' + cleaned.slice(12, 13);
        }
      } else {
        processedValue = '';
      }
    }

    // Format Phone: numeric only
    if (name === 'owner_phone') {
      processedValue = value.replace(/\D/g, '');
    }

    const updatedData = {
      ...formData,
      [name]: processedValue,
    };

    // Auto-calculate valid_to when vehicle type changes
    if (name === 'vehicle_type_id' && value) {
      const selectedVehicle = vehicleTypes.find((v) => v.id === parseInt(value));
      if (selectedVehicle && formData.valid_from) {
        const durationDays = selectedVehicle.permit_duration_days || 365;
        const fromDate = new Date(formData.valid_from);
        const toDate = new Date(fromDate);
        toDate.setDate(toDate.getDate() + durationDays);
        updatedData.valid_to = toDate.toISOString().split('T')[0];
      }
    }

    setFormData(updatedData);
  };

  const handleChalanInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Format CNIC
    if (name === 'owner_cnic') {
      const cleaned = value.replace(/\D/g, '');
      if (cleaned.length > 0) {
        if (cleaned.length <= 5) {
          processedValue = cleaned;
        } else if (cleaned.length <= 12) {
          processedValue = cleaned.slice(0, 5) + '-' + cleaned.slice(5);
        } else {
          processedValue = cleaned.slice(0, 5) + '-' + cleaned.slice(5, 12) + '-' + cleaned.slice(12, 13);
        }
      } else {
        processedValue = '';
      }
    }

    // Format Phone
    if (name === 'owner_phone') {
      processedValue = value.replace(/\D/g, '');
    }

    if (name === 'fees_amount' && autoCalcFee) {
      // When auto-calc is enabled, the fee is provided by the vehicle fee structure.
      return;
    }

    setChalanData({
      ...chalanData,
      [name]: processedValue,
    });
  };

  const handleChalanAttachmentChange = (e) => {
    const file = e.target.files?.[0] || null;
    setChalanAttachment(file);
    e.target.value = '';
  };

  const handleRemoveChalanAttachment = () => {
    setChalanAttachment(null);
  };

  const fetchVehicleFee = async (vehicleTypeId) => {
    try {
      setFeeLoading(true);
      const response = await vehicleFeeAPI.getByVehicleType(vehicleTypeId);
      const feeValue = response.data?.base_fee ?? response.data?.fee ?? response.data?.amount ?? null;
      setVehicleFee(response.data);
      if (feeValue !== null && feeValue !== undefined) {
        setChalanData(prev => ({
          ...prev,
          fees_amount: String(feeValue),
        }));
      }
    } catch (err) {
      setVehicleFee(null);
      console.warn(`Vehicle fee lookup failed for vehicle type ${vehicleTypeId}:`, err);
    } finally {
      setFeeLoading(false);
    }
  };

  useEffect(() => {
    if (!autoCalcFee) return;
    const selectedVehicleTypeId = chalanData.vehicle_type
      ? parseInt(chalanData.vehicle_type, 10)
      : formData.vehicle_type_id
        ? parseInt(formData.vehicle_type_id, 10)
        : null;

    if (selectedVehicleTypeId) {
      fetchVehicleFee(selectedVehicleTypeId);
    }
  }, [formData.vehicle_type_id, chalanData.vehicle_type, autoCalcFee]);

  const handleSubmit = async () => {
    try {
      // Check if chalan step is enabled and required
      const isChalanStepEnabled = enabledSteps.some(s => s.key === 'create_chalan');

      if (isChalanStepEnabled && !chalanAttachment) {
        setMessage({
          type: 'error',
          text: t('newPermit.chalanDocRequired'),
        });
        return;
      }

      setLoading(true);

      const vehicleTypeId = parseInt(formData.vehicle_type_id, 10);
      const permitTypeId = parseInt(formData.permit_type_id, 10);

      if (isNaN(vehicleTypeId) || isNaN(permitTypeId)) {
        setMessage({
          type: 'error',
          text: t('newPermit.invalidTypeSelection'),
        });
        setLoading(false);
        return;
      }

      // Safety: ensure authority is always a valid choice ('PTA' or 'RTA')
      const submitAuthority = ['PTA', 'RTA'].includes(formData.authority)
        ? formData.authority
        : 'RTA';

      const dataToSubmit = {
        vehicle_number: formData.vehicle_number,
        vehicle_type: vehicleTypeId,
        vehicle_make: formData.vehicle_make,
        vehicle_model: formData.vehicle_model,
        vehicle_year: formData.vehicle_year,
        vehicle_capacity: formData.vehicle_capacity,
        owner_name: formData.owner_name,
        owner_email: formData.owner_email,
        owner_cnic: formData.owner_cnic,
        owner_phone: formData.owner_phone,
        owner_address: formData.owner_address,
        authority: submitAuthority,
        permit_type: permitTypeId,
        valid_from: formData.valid_from,
        valid_to: formData.valid_to,
        description: formData.description,
        remarks: formData.remarks,
        restrictions: formData.restrictions,
        approved_routes: formData.approved_routes,
      };

      // If this is a renewal, use the reapply_for_expired endpoint
      let response;
      let permitId;

      if (renewalPermitId) {
        console.log(`Reapplying for expired permit: ${renewalPermitId}`);
        response = await apiClient.post(`/permits/${renewalPermitId}/reapply_for_expired/`, {
          valid_from: formData.valid_from,
          valid_to: formData.valid_to,
        });
        permitId = response.data.id;
        console.log(`Renewal permit created: ${permitId}`);
      } else {
        console.log('Creating new permit');
        response = await apiClient.post('/permits/', dataToSubmit);
        permitId = response.data.id;
      }

      // Create chalan if chalan step was enabled and data is provided
      let chalanCreated = false;
      let chalanId = null;

      const effectiveOwnerName = (chalanData.owner_name || formData.owner_name || '').trim();
      // Only create chalan if the step was enabled and attachment exists
      if (isChalanStepEnabled && effectiveOwnerName && chalanAttachment) {
        try {
          const chalanPayload = new FormData();
          chalanPayload.append('owner_name', effectiveOwnerName);
          chalanPayload.append('owner_cnic', chalanData.owner_cnic || formData.owner_cnic || '');
          chalanPayload.append('owner_phone', chalanData.owner_phone || formData.owner_phone || '');
          chalanPayload.append('car_number', chalanData.car_number || formData.vehicle_number || '');
          chalanPayload.append('permit', permitId);
          const resolvedVehicleType = parseInt(chalanData.vehicle_type || formData.vehicle_type_id);
          if (!isNaN(resolvedVehicleType)) {
            chalanPayload.append('vehicle_type', resolvedVehicleType);
          }
          chalanPayload.append('violation_description', chalanData.violation_description || '');

          let feesAmountValue = chalanData.fees_amount;
          if (!feesAmountValue && autoCalcFee && !isNaN(resolvedVehicleType)) {
            try {
              const feeResponse = await vehicleFeeAPI.getByVehicleType(resolvedVehicleType);
              feesAmountValue = feeResponse.data?.base_fee ?? feeResponse.data?.fee ?? feeResponse.data?.amount;
            } catch (feeErr) {
              console.warn('Failed to fetch vehicle fee before chalan creation:', feeErr);
            }
          }

          if (feesAmountValue !== undefined && feesAmountValue !== null && feesAmountValue !== '') {
            chalanPayload.append('fees_amount', parseFloat(feesAmountValue));
          }
          if (chalanData.remarks) {
            chalanPayload.append('remarks', chalanData.remarks);
          }
          if (chalanAttachment) {
            chalanPayload.append('document', chalanAttachment);
          }

          const chalanResponse = await chalanAPI.createChalan(chalanPayload);
          chalanId = chalanResponse.data.id;
          chalanCreated = true;
          console.log(`Chalan #${chalanId} created for permit #${permitId}`);
        } catch (chalanErr) {
          console.error('Error creating chalan:', chalanErr);
          const chalanErrMsg = chalanErr.response?.data
            ? JSON.stringify(chalanErr.response.data)
            : chalanErr.message;
          setMessage({ type: 'warning', text: t('newPermit.permitCreatedChalanFailed', { error: chalanErrMsg }) });
          setLoading(false);
          return;
        }
      }

      // Upload documents if any
      let uploadedDocCount = 0;
      let failedDocCount = 0;
      const failedDocs = [];
      const uploadedDocs = [];

      if (pendingDocuments.length > 0) {
        console.log(`Uploading ${pendingDocuments.length} documents for permit ${permitId}`);

        for (const doc of pendingDocuments) {
          const docFormData = new FormData();
          docFormData.append('file', doc.file);
          docFormData.append('permit', permitId);
          docFormData.append('document_type', doc.document_type || 'other');
          docFormData.append('filename', doc.filename);

          try {
            console.log(`Uploading document: ${doc.filename} (type=${doc.document_type})`);
            const docResponse = await apiClient.post('/permit-documents/', docFormData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
            console.log(`Document uploaded successfully:`, docResponse.data);
            uploadedDocCount++;
            uploadedDocs.push(doc.filename);
          } catch (docErr) {
            console.error(`Error uploading document ${doc.filename}:`, docErr);
            console.error(`Response data:`, docErr.response?.data);
            failedDocCount++;
            // Extract the most useful error message from DRF's nested error payload.
            let detail = 'Upload failed';
            const respData = docErr.response?.data;
            if (respData) {
              if (typeof respData === 'string') {
                detail = respData;
              } else if (respData.document_type) {
                const dtErr = Array.isArray(respData.document_type)
                  ? respData.document_type[0]
                  : respData.document_type;
                detail = `document_type: ${dtErr}`;
              } else if (respData.detail) {
                detail = respData.detail;
              } else if (respData.message) {
                detail = respData.message;
              }
            }
            failedDocs.push({ filename: doc.filename, document_type: doc.document_type, detail });
          }
        }
      }

      // Save created IDs for later use
      setCreatedPermitId(permitId);
      if (chalanCreated) {
        setCreatedChalanId(chalanId);
      }

      // Prepare success/warning message
      let resultMessage = t('newPermit.permitCreatedMsg', { id: permitId });
      if (chalanCreated) {
        resultMessage += t('newPermit.chalanCreatedSuffix', { id: chalanId });
      }
      if (pendingDocuments.length > 0) {
        resultMessage += t('newPermit.docsUploadedSuffix', { count: uploadedDocCount });
        if (failedDocCount > 0) {
          resultMessage += t('newPermit.docsFailedSuffix', { count: failedDocCount });
          // Surface the first failure so the user knows which doc and why.
          const first = failedDocs[0];
          resultMessage += t('newPermit.docFailDetail', { filename: first.filename, type: first.document_type, detail: first.detail });
        }
      }

      setMessage({ type: failedDocCount > 0 ? 'warning' : 'success', text: resultMessage });
      setConfirmDialogOpen(false);

      // Reset form and documents
      setFormData({
        authority: currentAuthority,
        permit_type_id: '',
        vehicle_number: '',
        vehicle_type_id: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_year: new Date().getFullYear(),
        vehicle_capacity: '',
        owner_name: '',
        owner_cnic: '',
        owner_email: '',
        owner_phone: '',
        owner_address: '',
        description: '',
        remarks: '',
        restrictions: '',
        approved_routes: '',
        valid_from: new Date().toISOString().split('T')[0],
        valid_to: '',
      });
      setPendingDocuments([]);
      setChalanData({
        owner_name: '',
        owner_cnic: '',
        owner_phone: '',
        car_number: '',
        permit: '',
        vehicle_type: '',
        violation_description: '',
        fees_amount: '',
        remarks: '',
      });
      setChalanAttachment(null);
      setAutoCalcFee(true);
      setActiveStep(0);
    } catch (err) {
      console.error('Error creating permit:', err);
      const errorMessage = extractErrorMessage(err);
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // Clears the wizard back to a blank permit, discarding whatever draft/permit
  // was being edited so the next "New Permit" click actually starts fresh
  // instead of reusing the previous form's leftover state.
  const resetPermitForm = () => {
    setFormData({
      authority: currentAuthority,
      permit_type_id: '',
      vehicle_number: '',
      vehicle_type_id: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_year: new Date().getFullYear(),
      vehicle_capacity: '',
      owner_name: isOwnerRole
        ? (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username || '')
        : '',
      owner_cnic: '',
      owner_email: isOwnerRole ? (user.email || '') : '',
      owner_phone: '',
      owner_address: '',
      description: '',
      remarks: '',
      restrictions: '',
      approved_routes: '',
      valid_from: new Date().toISOString().split('T')[0],
      valid_to: '',
    });
    setPendingDocuments([]);
    setCurrentDraftId(null);
    setCreatedPermitId(null);
    setCreatedChalanId(null);
    setActiveStep(0);
  };

  const handleStartNewPermit = () => {
    resetPermitForm();
    setMessage(null);
    setViewMode('new');
  };

  // UI-only: just triggers the browser's native camera permission prompt.
  const handleScanVehicleClick = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (err) {
      // Permission denied or no camera — nothing further to do here.
    }
  };

  // UI-only: opens the native file chooser for a CSV file.
  const handleBulkPermitClick = () => {
    csvInputRef.current?.click();
  };

  const handleSaveDraft = async () => {
    try {
      setDraftSaving(true);

      // Only send the fields that have values to avoid validation errors
      const draftPayload = {
        authority: formData.authority || currentAuthority,
        permit_type_id: formData.permit_type_id ? parseInt(formData.permit_type_id, 10) : null,
        vehicle_number: formData.vehicle_number || null,
        vehicle_type_id: formData.vehicle_type_id ? parseInt(formData.vehicle_type_id, 10) : null,
        vehicle_make: formData.vehicle_make || null,
        vehicle_model: formData.vehicle_model || null,
        vehicle_year: formData.vehicle_year || null,
        vehicle_capacity: formData.vehicle_capacity || null,
        owner_name: formData.owner_name || null,
        owner_cnic: formData.owner_cnic || null,
        owner_email: formData.owner_email || null,
        owner_phone: formData.owner_phone || null,
        owner_address: formData.owner_address || null,
        description: formData.description || null,
        remarks: formData.remarks || null,
        restrictions: formData.restrictions || null,
        approved_routes: formData.approved_routes || null,
        valid_from: formData.valid_from || null,
        valid_to: formData.valid_to || null,
        wizard_step: activeStep,
      };

      // Include draft ID if updating existing draft
      if (currentDraftId) {
        draftPayload.id = currentDraftId;
      }

      console.log('Saving draft with payload:', draftPayload);
      const response = await apiClient.post('/permits/save_draft/', draftPayload);
      const draftId = response.data.draft.id;
      setCurrentDraftId(draftId);
      fetchUserDrafts();

      setMessage({
        type: 'success',
        text: t('newPermit.draftSavedMsg', { id: draftId }),
      });
    } catch (err) {
      console.error('Error saving draft:', err);
      console.error('Error response:', err.response?.data);
      const rawErr = err.response?.data?.errors || err.response?.data?.message || err.response?.data?.detail;
      const errorMessage = rawErr
        ? (typeof rawErr === 'object' ? JSON.stringify(rawErr) : rawErr)
        : t('newPermit.draftSaveFailedGeneric');
      setMessage({ type: 'error', text: t('newPermit.errorPrefix', { message: errorMessage }) });
    } finally {
      setDraftSaving(false);
    }
  };

  // Print chalan in three formats (Bank, Department, End User)
  const printChalan = () => {
    if (!createdChalanId || !createdPermitId) {
      alert(t('newPermit.noChalanToPrint'));
      return;
    }

    const getChalanHeader = (type) => {
      const titles = {
        bank: t('newPermit.chalanForBank'),
        department: t('newPermit.chalanForDepartment'),
        enduser: t('newPermit.chalanForEndUser'),
      };
      return titles[type] || titles.bank;
    };

    const generateChalanHTML = (type) => {
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      const effectiveChalanAmount = chalanData.fees_amount != null && chalanData.fees_amount !== ''
        ? parseFloat(chalanData.fees_amount)
        : vehicleFee?.base_fee != null
          ? parseFloat(vehicleFee.base_fee)
          : vehicleFee?.fee != null
            ? parseFloat(vehicleFee.fee)
            : vehicleFee?.amount != null
              ? parseFloat(vehicleFee.amount)
              : 0;
      const amountString = effectiveChalanAmount !== null && !Number.isNaN(effectiveChalanAmount)
        ? effectiveChalanAmount.toLocaleString('en-US')
        : t('permitSearch.na');

      return `
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${t('newPermit.singleCopyTitle', { id: createdChalanId, type: type.toUpperCase() })}</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .page-break {
              page-break-after: always;
              margin-bottom: 40px;
              padding-bottom: 40px;
              border-bottom: 2px solid #ccc;
            }
            .chalan-container {
              max-width: 700px;
              margin: 0 auto;
              border: 3px solid #1976d2;
              padding: 30px;
              background: linear-gradient(135deg, #f5f7fa 0%, #c8e6c9 100%);
              border-radius: 10px;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 3px double #1976d2;
            }
            .header h1 {
              margin: 0;
              color: #1976d2;
              font-size: 24px;
              text-transform: uppercase;
              letter-spacing: 1px;
              font-weight: 800;
            }
            .header p {
              margin: 5px 0;
              color: #666;
              font-size: 12px;
            }
            .receipt-no {
              text-align: center;
              margin: 15px 0;
              background: #fff9c4;
              padding: 10px;
              border-radius: 5px;
              font-weight: bold;
              border: 2px solid #f57f17;
            }
            .section {
              margin: 20px 0;
              padding: 15px;
              background: white;
              border-radius: 5px;
              border-left: 5px solid #1976d2;
            }
            .section-title {
              font-weight: bold;
              color: #1976d2;
              margin-bottom: 10px;
              font-size: 14px;
              text-transform: uppercase;
            }
            .row {
              display: flex;
              justify-content: space-between;
              margin: 8px 0;
              font-size: 13px;
              padding: 5px 0;
              border-bottom: 1px dotted #ddd;
            }
            .label {
              font-weight: 600;
              color: #333;
              flex: 0 0 40%;
            }
            .value {
              text-align: right;
              flex: 0 0 60%;
              color: #555;
            }
            .amount-section {
              background: linear-gradient(135deg, #ffeb3b 0%, #fbc02d 100%);
              padding: 15px;
              border-radius: 5px;
              margin: 20px 0;
              text-align: center;
              border: 2px solid #f57f17;
            }
            .amount-label {
              font-size: 12px;
              color: #666;
              margin-bottom: 5px;
            }
            .amount {
              font-size: 28px;
              font-weight: bold;
              color: #d32f2f;
            }
            .footer {
              margin-top: 30px;
              padding-top: 20px;
              border-top: 2px solid #1976d2;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            .footer-note {
              margin-top: 10px;
              padding: 10px;
              background: #f5f5f5;
              border-radius: 5px;
              font-size: 11px;
              line-height: 1.6;
            }
            .signature-box {
              display: flex;
              justify-content: space-around;
              margin-top: 40px;
              padding-top: 30px;
            }
            .signature {
              text-align: center;
              width: 45%;
            }
            .signature-line {
              border-top: 2px solid #333;
              margin-top: 30px;
              padding-top: 5px;
              font-weight: 600;
              font-size: 12px;
            }
            .watermark {
              opacity: 0.1;
              position: fixed;
              font-size: 80px;
              color: #1976d2;
              transform: rotate(-45deg);
              left: 50%;
              top: 50%;
              z-index: -1;
            }
            @media print {
              body { margin: 0; padding: 0; }
              .page-break { page-break-after: always; }
              .watermark { position: fixed; }
            }
          </style>
        </head>
        <body>
          <div class="watermark">${type.toUpperCase()}</div>
          
          <div class="chalan-container">
            <div class="header">
              <h1>${getChalanHeader(type)}</h1>
              <p>${t('newPermit.provincialRegionalAuthority')}</p>
              <p>${t('newPermit.permitVehicleManagementSystem')}</p>
            </div>

            <div class="receipt-no">
              ${t('newPermit.chalanReceiptNo', { id: createdChalanId, letter: type.substring(0, 1).toUpperCase() })}
            </div>

            <div class="section">
              <div class="section-title">${t('newPermit.vehicleInfoSectionTitle')}</div>
              <div class="row">
                <div class="label">${t('newPermit.registrationNumberLabel')}</div>
                <div class="value">${chalanData.car_number}</div>
              </div>
              <div class="row">
                <div class="label">${t('newPermit.vehicleTypeColonLabel')}</div>
                <div class="value">${vehicleTypes.find(v => v.id === parseInt(chalanData.vehicle_type))?.name || t('permitSearch.na')}</div>
              </div>
              <div class="row">
                <div class="label">${t('newPermit.permitNumberColonLabel')}</div>
                <div class="value">${createdPermitId}</div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">${t('newPermit.ownerInfoHeaderPerson')}</div>
              <div class="row">
                <div class="label">${t('newPermit.nameColonLabel')}</div>
                <div class="value">${chalanData.owner_name}</div>
              </div>
              <div class="row">
                <div class="label">${t('newPermit.cnicColonLabel')}</div>
                <div class="value">${chalanData.owner_cnic}</div>
              </div>
              <div class="row">
                <div class="label">${t('permitEdit.contactLabel')}</div>
                <div class="value">${chalanData.owner_phone}</div>
              </div>
            </div>

            <div class="section">
              <div class="section-title">${t('newPermit.feeDescriptionSectionTitle')}</div>
              <div style="padding: 10px; background: #f5f5f5; border-radius: 5px; min-height: 40px;">
                ${chalanData.violation_description || t('newPermit.feeDescFallback')}
              </div>
            </div>

            <div class="amount-section">
              <div class="amount-label">${t('newPermit.totalFeeChargesLabel')}</div>
              <div class="amount">PKR ${amountString}</div>
              <div style="font-size: 11px; margin-top: 5px; color: #666;">
                ${t('permitEdit.dateLabel', { date: currentDate })}
              </div>
            </div>

            <div class="section" style="background: #e3f2fd;">
              <div class="section-title">${t('newPermit.paymentInstructionsTitle')}</div>
              <div style="font-size: 12px; line-height: 1.8; color: #333;">
                <div style="margin-bottom: 8px;">
                  ${t('newPermit.paidWithin30Days')}
                </div>
                <div style="margin-bottom: 8px;">
                  ${t('newPermit.paymentAtBanks')}
                </div>
                <div style="margin-bottom: 8px;">
                  ${t('newPermit.keepReceiptProof')}
                </div>
                <div style="margin-bottom: 8px;">
                  ${t('newPermit.contactTransportAuthority')}
                </div>
              </div>
            </div>

            <div class="footer">
              <p>${t('newPermit.officialChalanGenerated', { date: currentDate })}</p>
              <p>${t('newPermit.printedForLabel')} <strong>${type === 'bank' ? t('newPermit.bankSubmissionLabel') : type === 'department' ? t('newPermit.departmentRecordLabel') : t('newPermit.vehicleOwnerLabel')}</strong></p>
              <div class="footer-note">
                <strong>${t('newPermit.noteLabel')}</strong>${t('newPermit.retainChalanNote')}
              </div>
            </div>

            <div class="signature-box">
              <div class="signature">
                <div class="signature-line">${t('newPermit.authorizedOfficerLabel')}</div>
              </div>
              <div class="signature">
                <div class="signature-line">${t('newPermit.receivedByLabel')}</div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;
    };

    // Generate all three versions
    const bankHTML = generateChalanHTML('bank');
    const departmentHTML = generateChalanHTML('department');
    const enduserHTML = generateChalanHTML('enduser');

    // Combine all three with page breaks
    const fullHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${t('newPermit.allCopiesTitle', { id: createdChalanId })}</title>
        <style>
          @media print {
            body { margin: 0; padding: 0; }
            .page-break { page-break-after: always; }
          }
        </style>
      </head>
      <body>
        ${bankHTML}
        <div style="page-break-after: always; margin: 40px 0; border-bottom: 2px solid #999;"></div>
        ${departmentHTML}
        <div style="page-break-after: always; margin: 40px 0; border-bottom: 2px solid #999;"></div>
        ${enduserHTML}
      </body>
      </html>
    `;

    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(fullHTML);
    printWindow.document.close();

    // Auto print after a short delay
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Print chalan preview in wizard step with bank info and warnings
  const printChalanPreview = () => {
    // Compute the actual fee amount from chalan form data or fetched vehicle fee
    const feeCandidate = chalanData.fees_amount != null && chalanData.fees_amount !== ''
      ? chalanData.fees_amount
      : vehicleFee?.base_fee ?? vehicleFee?.fee ?? vehicleFee?.amount ?? 0;
    const chalanFeeValue = feeCandidate != null && feeCandidate !== ''
      ? parseFloat(feeCandidate)
      : NaN;
    const displayFee = !Number.isNaN(chalanFeeValue)
      ? `PKR ${chalanFeeValue.toLocaleString('en-US')}`
      : feeLoading
        ? t('newPermit.loadingFeeEllipsis')
        : t('newPermit.autoCalculatedLabel');

    // Validate required fields (check both chalanData and pre-filled formData)
    const ownerName = (chalanData.owner_name || formData.owner_name).trim();
    const carNumber = (chalanData.car_number || formData.vehicle_number).trim();
    const vehicleType = chalanData.vehicle_type || formData.vehicle_type_id;
    const feeDescription = (chalanData.violation_description).trim();

    if (!ownerName || !carNumber || !vehicleType || !feeDescription) {
      alert(t('newPermit.fillRequiredBeforePrint'));
      return;
    }

    const getChalanCopy = (type) => {
      const titles = {
        bank: t('newPermit.bankCopyLabel'),
        user: t('newPermit.userCopyLabel'),
        department: t('newPermit.departmentCopyLabel'),
      };
      const colors = {
        bank: '#e3f2fd',
        user: '#f3e5f5',
        department: '#e8f5e9',
      };
      const borders = {
        bank: '#1976d2',
        user: '#7b1fa2',
        department: '#388e3c',
      };

      return `
        <div style="flex: 1; border: 3px solid ${borders[type]}; padding: 15px; margin: 0 10px; background: ${colors[type]}; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <div style="text-align: center; margin-bottom: 12px; border-bottom: 2px solid ${borders[type]}; padding-bottom: 8px;">
            <h3 style="margin: 0; color: ${borders[type]}; font-size: 14px; text-transform: uppercase; letter-spacing: 0.5px;">${titles[type]}</h3>
            <p style="margin: 3px 0; color: #666; font-size: 10px;">${t('newPermit.paymentReceiptLabel')}</p>
          </div>

          <div style="font-size: 11px; line-height: 1.5;">
            <!-- Owner Info -->
            <div style="margin-bottom: 10px; padding: 8px; background: white; border-radius: 4px;">
              <div style="font-weight: bold; color: ${borders[type]}; margin-bottom: 4px; font-size: 10px;">${t('newPermit.ownerSectionLabel')}</div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 10px;"><span style="font-weight: 600;">${t('newPermit.nameColonLabel')}</span><span style="text-align: right;">${chalanData.owner_name || formData.owner_name}</span></div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 10px;"><span style="font-weight: 600;">${t('newPermit.cnicColonLabel')}</span><span style="text-align: right; font-size: 9px;">${chalanData.owner_cnic || formData.owner_cnic}</span></div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 10px;"><span style="font-weight: 600;">${t('newPermit.phoneColonLabel')}</span><span style="text-align: right;">${chalanData.owner_phone || formData.owner_phone}</span></div>
            </div>

            <!-- Vehicle Info -->
            <div style="margin-bottom: 10px; padding: 8px; background: white; border-radius: 4px;">
              <div style="font-weight: bold; color: ${borders[type]}; margin-bottom: 4px; font-size: 10px;">${t('newPermit.vehicleSectionLabel')}</div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 10px;"><span style="font-weight: 600;">${t('newPermit.regColonLabel')}</span><span style="text-align: right;">${chalanData.car_number || formData.vehicle_number}</span></div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 10px;"><span style="font-weight: 600;">${t('newPermit.typeColonLabel')}</span><span style="text-align: right; font-size: 9px;">${vehicleTypes.find(v => v.id === parseInt(chalanData.vehicle_type || formData.vehicle_type_id))?.name || t('permitSearch.na')}</span></div>
            </div>

            <!-- Fee Description -->
            <div style="margin-bottom: 10px; padding: 8px; background: white; border-radius: 4px;">
              <div style="font-weight: bold; color: ${borders[type]}; margin-bottom: 3px; font-size: 10px;">${t('newPermit.feeSectionLabel')}</div>
              <div style="font-size: 10px; line-height: 1.3;">${chalanData.violation_description || t('newPermit.feeDescFallback')}</div>
            </div>

            <!-- Amount -->
            <div style="background: linear-gradient(135deg, #ffeb3b 0%, #fbc02d 100%); padding: 10px; text-align: center; border-radius: 4px; margin-bottom: 10px; border: 2px solid #f57f17;">
              <div style="font-size: 9px; color: #666; margin-bottom: 3px;">${t('newPermit.totalDueLabel')}</div>
              <div style="font-size: 20px; font-weight: bold; color: #d32f2f;">${displayFee}</div>
            </div>

            <!-- Bank Account Information -->
            <div style="margin-bottom: 10px; padding: 8px; background: #e3f2fd; border-radius: 4px; border-left: 3px solid #1976d2;">
              <div style="font-weight: bold; color: #1976d2; margin-bottom: 3px; font-size: 10px;">${t('newPermit.bankDetailsLabel')}</div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 9px;"><span style="font-weight: 600;">${t('newPermit.bankColonLabel')}</span><span style="text-align: right;">State Bank of Pakistan</span></div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 9px;"><span style="font-weight: 600;">${t('newPermit.accountColonLabel')}</span><span style="text-align: right; font-size: 8px;">Transport Authority</span></div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 9px;"><span style="font-weight: 600;">${t('newPermit.acNoColonLabel')}</span><span style="text-align: right; font-size: 8px;">12345-6789</span></div>
              <div style="display: flex; justify-content: space-between; margin: 2px 0; font-size: 9px;"><span style="font-weight: 600;">${t('newPermit.ibanColonLabel')}</span><span style="text-align: right; font-size: 8px;">PK36SBOP1234</span></div>
            </div>

            <!-- Payment Reminder (varies by copy) -->
            ${type === 'bank' ? `
              <div style="background: #e3f2fd; padding: 6px; border-left: 3px solid #1976d2; border-radius: 3px; font-size: 9px; color: #0d47a1;">
                <strong>${t('newPermit.forBankLabel')}</strong>${t('newPermit.forBankInstruction')}
              </div>
            ` : type === 'user' ? `
              <div style="background: #ffebee; padding: 6px; border-left: 3px solid #d32f2f; border-radius: 3px; font-size: 9px; color: #b71c1c;">
                <strong>${t('newPermit.criticalLabel')}</strong>${t('newPermit.payWithin30OrRejected')}
              </div>
            ` : `
              <div style="background: #e8f5e9; padding: 6px; border-left: 3px solid #388e3c; border-radius: 3px; font-size: 9px; color: #1b5e20;">
                <strong>${t('newPermit.departmentColonLabel')}</strong>${t('newPermit.officialRecordsNote')}
              </div>
            `}
          </div>
        </div>
      `;
    };

    const chalanHTML = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${t('newPermit.threeCopiesTitle')}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 15px;
            padding: 0;
            background: white;
          }
          .chalan-container {
            display: flex;
            gap: 10px;
            justify-content: center;
            align-items: flex-start;
          }
          @media print {
            body { margin: 10px; padding: 0; }
            .chalan-container { gap: 8px; }
          }
        </style>
      </head>
      <body>
        <div class="chalan-container">
          ${getChalanCopy('bank')}
          ${getChalanCopy('user')}
          ${getChalanCopy('department')}
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(chalanHTML);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Render step components based on step key
  const renderStep = () => {
    const stepKey = getStepKey(activeStep);
    switch (stepKey) {
      case 'permit_configuration':
        return renderPermitConfiguration();
      case 'vehicle_details':
        return renderVehicleDetails();
      case 'owner_information':
        return renderOwnerInformation();
      case 'additional_info':
        return renderAdditionalInfo();
      case 'create_chalan':
        return renderChalanForm();
      case 'documents_submission':
        return renderDocuments();
      case 'review_submit':
        return renderReviewSubmit();
      default:
        return null;
    }
  };

  const renderPermitConfiguration = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
        {t('newPermit.permitConfigHeader')}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth disabled sx={{ width: '100%', minWidth: 0 }}>
            <InputLabel>{t('permitEdit.authority')}</InputLabel>
            <Select
              fullWidth
              name="authority"
              value={formData.authority}
              onChange={handleChange}
              label={t('permitEdit.authority')}
              disabled
              sx={{ width: '100%', minWidth: '200px', '& .MuiSelect-select': { width: '100%' } }}
              MenuProps={{ PaperProps: { sx: { minWidth: 300 } } }}
            >
              <MenuItem value="PTA">{t('permitEdit.provincialAuthority')}</MenuItem>
              <MenuItem value="RTA">{t('permitEdit.regionalAuthority')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth required error={formData.permit_type_id === ''} sx={{ width: '100%', minWidth: '200px' }}>
            <InputLabel>{t('permitEdit.permitType')}</InputLabel>
            <Select
              fullWidth
              name="permit_type_id"
              value={formData.permit_type_id}
              onChange={handleChange}
              label={t('permitEdit.permitType')}
              size="medium"
              sx={{ width: '100%', minWidth: '100px', '& .MuiSelect-select': { width: '100%' } }}
              MenuProps={{ PaperProps: { sx: { minWidth: 300 } } }}
            >
              <MenuItem value="">
                <em>{t('permitEdit.selectPermitType')}</em>
              </MenuItem>
              {permitTypes.map((type) => (
                <MenuItem key={type.id} value={String(type.id)}>
                  {type.name} ({type.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Paper
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          borderLeft: '4px solid #1976d2',
        }}
      >
        <Typography variant="body2" sx={{ color: '#0d47a1' }}>
          <strong>{t('newPermit.infoLabel')}</strong>{t('newPermit.selectPermitTypeInfo')}
        </Typography>
      </Paper>
    </Box>
  );

  const renderVehicleDetails = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
        {t('permitModal.vehicleDetailsHeader')}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.vehicleNumberLabel')}
            name="vehicle_number"
            value={formData.vehicle_number}
            onChange={handleChange}
            placeholder={t('newPermit.vehicleNumberPlaceholderShort')}
            variant="outlined"
            inputProps={{ maxLength: 20 }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={formData.vehicle_type_id === ''} sx={{ width: '100%', minWidth: '200px' }}>
            <InputLabel>{t('permitEdit.vehicleType')}</InputLabel>
            <Select
              fullWidth
              name="vehicle_type_id"
              value={formData.vehicle_type_id}
              onChange={handleChange}
              label={t('permitEdit.vehicleType')}
              sx={{ width: '100%', minWidth: '100px', '& .MuiSelect-select': { width: '100%' } }}
              MenuProps={{ PaperProps: { sx: { minWidth: 300 } } }}
            >
              <MenuItem value="">{t('permitEdit.selectType')}</MenuItem>
              {vehicleTypes.map((type) => (
                <MenuItem key={type.id} value={String(type.id)}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.makeLabel')}
            name="vehicle_make"
            value={formData.vehicle_make}
            onChange={handleChange}
            placeholder={t('newPermit.makePlaceholderShort')}
            variant="outlined"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.modelLabel')}
            name="vehicle_model"
            value={formData.vehicle_model}
            onChange={handleChange}
            placeholder={t('newPermit.modelPlaceholderShort')}
            variant="outlined"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.yearLabel')}
            name="vehicle_year"
            type="number"
            value={formData.vehicle_year}
            onChange={handleChange}
            inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
            variant="outlined"
            required
            sx={{ width: '100%', minWidth: '200px' }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.capacityLabel')}
            name="vehicle_capacity"
            type="number"
            value={formData.vehicle_capacity}
            onChange={handleChange}
            inputProps={{ min: 1, max: 300 }}
            variant="outlined"
            required
            sx={{ width: '100%', minWidth: '200px' }}
          />
        </Grid>
      </Grid>
      {formData.vehicle_type_id && formData.valid_from && (
        <Paper
          sx={{
            p: 2.5,
            background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
            borderLeft: '4px solid #ff9800',
          }}
        >
          <Typography variant="caption" sx={{ color: '#e65100', display: 'block', fontWeight: 600, mb: 1 }}>
            {t('newPermit.permitDurationLabel')}
          </Typography>
          <Typography variant="body2" sx={{ color: '#d84315' }}>
            <strong>
              {vehicleTypes.find((v) => v.id === parseInt(formData.vehicle_type_id))?.name}:
            </strong>{' '}
            {formData.valid_to &&
              t('newPermit.validUntilLabel', {
                date: new Date(formData.valid_to).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              })}
          </Typography>
        </Paper>
      )}
    </Box>
  );

  const renderOwnerInformation = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
        {t('newPermit.ownerInfoHeaderPerson')}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.fullNameLabel')}
            name="owner_name"
            value={formData.owner_name}
            onChange={handleChange}
            placeholder={t('permitEdit.fullNamePlaceholder')}
            variant="outlined"
            inputProps={{ maxLength: 100 }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.cnicLabel')}
            name="owner_cnic"
            value={formData.owner_cnic}
            onChange={handleChange}
            placeholder={t('permitEdit.cnicPlaceholder')}
            variant="outlined"
            inputProps={{ maxLength: 20 }}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.emailLabel')}
            name="owner_email"
            type="email"
            value={formData.owner_email}
            onChange={handleChange}
            placeholder={t('permitEdit.emailPlaceholder')}
            variant="outlined"
            error={isEmailInvalid}
            helperText={isEmailInvalid ? t('newPermit.enterValidEmail') : ''}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.phoneLabel')}
            name="owner_phone"
            value={formData.owner_phone}
            onChange={handleChange}
            placeholder={t('newPermit.phonePlaceholderShort')}
            variant="outlined"
            inputProps={{ maxLength: 20 }}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('permitEdit.addressLabel')}
            name="owner_address"
            multiline
            rows={3}
            value={formData.owner_address}
            onChange={handleChange}
            placeholder={t('permitEdit.addressPlaceholder')}
            variant="outlined"
            required
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderAdditionalInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
        {t('newPermit.additionalInfoHeader')}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('permitEdit.descriptionLabel')}
            name="description"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleChange}
            placeholder={t('newPermit.descPlaceholderShort')}
            variant="outlined"
            required
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('permitEdit.remarksLabel')}
            name="remarks"
            multiline
            rows={2}
            value={formData.remarks}
            onChange={handleChange}
            placeholder={t('permitEdit.remarksPlaceholder')}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth variant="outlined" sx={{ width: '100%', minWidth: '200px' }}>
            <InputLabel>{t('permitEdit.approvedRoutesLabel')}</InputLabel>
            <Select
              fullWidth
              multiple
              name="approved_routes"
              value={formData.approved_routes ? formData.approved_routes.split(', ').filter(Boolean) : []}
              onChange={(e) => {
                const { value } = e.target;
                setFormData(prev => ({
                  ...prev,
                  approved_routes: typeof value === 'string' ? value : value.join(', ')
                }));
              }}
              label={t('permitEdit.approvedRoutesLabel')}
              sx={{ width: '100%', minWidth: '100px', '& .MuiSelect-select': { width: '100%' } }}
              MenuProps={{ PaperProps: { sx: { minWidth: 300 } } }}
              renderValue={(selected) => selected.join(', ')}
            >
              {routesList.map(route => (
                <MenuItem key={route.id} value={route.name}>
                  {route.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitEdit.restrictionsLabel')}
            name="restrictions"
            multiline
            rows={2}
            value={formData.restrictions}
            onChange={handleChange}
            placeholder={t('newPermit.restrictionsPlaceholderShort')}
            variant="outlined"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const handleFileUpload = (e, documentType) => {
    const file = e.target.files[0];
    if (!file) return;

    // Replace existing entry for this document type, or add new
    setPendingDocuments(prev => {
      const filtered = prev.filter(d => d.document_type !== documentType);
      return [...filtered, {
        id: Date.now() + Math.random(),
        file,
        filename: file.name,
        size: file.size,
        document_type: documentType,
      }];
    });
    e.target.value = '';
  };

  const handleRemoveDocument = (documentType) => {
    setPendingDocuments(prev => prev.filter(d => d.document_type !== documentType));
  };

  const renderDocuments = () => {
    // Build a label map from API-fetched document types, falling back to key
    const docTypeLabelMap = documentTypes.reduce((acc, dt) => {
      acc[dt.key] = dt.name;
      return acc;
    }, {});

    // Normalise: old format was plain strings, new format is {key, required}
    const normalise = (val) => {
      if (!Array.isArray(val)) return [];
      return val.map(item => typeof item === 'string' ? { key: item, required: true } : item);
    };

    const selectedPermitType = permitTypes.find(t => t.id === parseInt(formData.permit_type_id, 10));
    const docTypeEntries = selectedPermitType?.required_document_types?.length
      ? normalise(selectedPermitType.required_document_types)
      : documentTypes.map(dt => ({ key: dt.key, required: dt.is_required !== false }));

    const missingCompulsory = docTypeEntries.filter(
      ({ key, required }) => required && !pendingDocuments.some(d => d.document_type === key)
    );

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
          {t('newPermit.documentsSubmissionHeader')}
        </Typography>
        {selectedPermitType && (
          <Typography variant="body2" sx={{ color: '#555' }}>
            {t('newPermit.uploadDocsForType', { name: selectedPermitType.name })}
          </Typography>
        )}

        {missingCompulsory.length > 0 && (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {t('newPermit.compulsoryDocsWarning', {
              count: missingCompulsory.length,
              list: missingCompulsory.map(({ key }) => docTypeLabelMap[key] || key).join(', '),
            })}
          </Alert>
        )}

        <Grid container spacing={2}>
          {docTypeEntries.map(({ key: docType, required: isRequired }) => {
            const uploaded = pendingDocuments.find(d => d.document_type === docType);
            const inputId = `doc-upload-${docType}`;
            return (
              <Grid item xs={12} sm={6} key={docType}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    borderColor: uploaded ? '#2e7d32' : '#1976d2',
                    borderStyle: 'dashed',
                    background: uploaded ? '#f1f8e9' : '#f5f9ff',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: uploaded ? '#2e7d32' : '#1565c0', flexGrow: 1 }}>
                      {uploaded ? '✅' : '📄'} {docTypeLabelMap[docType] || docType}
                    </Typography>
                    <Chip
                      label={isRequired ? t('permitEdit.compulsory') : t('permitEdit.optional')}
                      size="small"
                      color={isRequired ? 'error' : 'success'}
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  </Box>

                  {uploaded ? (
                    <Box>
                      <Typography variant="body2" sx={{ color: '#333', wordBreak: 'break-all', mb: 0.5 }}>
                        {uploaded.filename}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#777' }}>
                        {(uploaded.size / 1024).toFixed(1)} KB
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveDocument(docType)}
                        >
                          {t('permitEdit.remove')}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          component="label"
                          htmlFor={inputId}
                        >
                          {t('permitEdit.replace')}
                          <input
                            type="file"
                            id={inputId}
                            hidden
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) => handleFileUpload(e, docType)}
                          />
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                        {t('permitEdit.maxFileSizeNote')}
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        component="label"
                        htmlFor={inputId}
                        sx={{ bgcolor: '#1976d2' }}
                      >
                        {t('permitEdit.chooseFile')}
                        <input
                          type="file"
                          id={inputId}
                          hidden
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={(e) => handleFileUpload(e, docType)}
                        />
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderReviewSubmit = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
        {t('newPermit.reviewSubmitHeader')}
      </Typography>
      <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)' }}>
        <Grid container spacing={3}>
          {/* Permit Configuration */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1.5 }}>
              {t('newPermit.permitConfigHeader')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('permitEdit.authority')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formData.authority === 'PTA' ? t('permitEdit.provincialAuthority') : t('permitEdit.regionalAuthority')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('permitEdit.permitType')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {permitTypes.find((pt) => pt.id === parseInt(formData.permit_type_id))?.name}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Vehicle Details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1.5 }}>
              {t('permitModal.vehicleDetailsHeader')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('newPermit.vehicleField')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formData.vehicle_make} {formData.vehicle_model} ({formData.vehicle_year})
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('newPermit.registrationField')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formData.vehicle_number}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('permitEdit.vehicleType')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {vehicleTypes.find((v) => v.id === parseInt(formData.vehicle_type_id))?.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('newPermit.capacityFieldShort')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('permitEdit.seatsUnit', { count: formData.vehicle_capacity })}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Owner Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1.5 }}>
              {t('newPermit.ownerInfoHeaderPerson')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('permitDetails.nameField')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formData.owner_name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('permitDetails.cnicField')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formData.owner_cnic}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('permitDetails.emailField')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formData.owner_email || t('permitSearch.na')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  {t('permitDetails.phoneField')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formData.owner_phone}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Documents */}
          {pendingDocuments.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2', mb: 1.5 }}>
                {t('newPermit.documentsToUploadHeader')}
              </Typography>
              <Grid container spacing={2}>
                {pendingDocuments.map((doc) => (
                  <Grid item xs={12} sm={6} key={doc.id}>
                    <Paper
                      sx={{
                        p: 2,
                        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                        borderLeft: '4px solid #4caf50',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#1b5e20', display: 'block' }}>
                        {t('newPermit.fileLabel')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32', wordBreak: 'break-word' }}>
                        {doc.filename}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#558b2f', display: 'block', mt: 0.5 }}>
                        {(doc.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 3, p: 2, background: 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)', borderRadius: '8px' }}>
          <Typography variant="body2" sx={{ color: '#1b5e20', fontWeight: 600 }}>
            {t('newPermit.reviewSubmitNotice')}{pendingDocuments.length > 0 ? t('newPermit.andUploadDocsSuffix', { count: pendingDocuments.length }) : ''}.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );

  const renderChalanForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
        {t('newPermit.step5Title')}
      </Typography>

      <Paper
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
          borderLeft: '4px solid #4caf50',
        }}
      >
        <Typography variant="body2" sx={{ color: '#1b5e20' }}>
          <strong>{t('newPermit.autoFilledLabel')}</strong>{t('newPermit.autoFilledDesc')}
        </Typography>
      </Paper>

      {!isEmployeeOrAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{t('newPermit.noticeLabel')}</strong>{t('newPermit.endUserChalanNotice')}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('printCertificate.ownerNameLabel')}
            name="owner_name"
            value={chalanData.owner_name || formData.owner_name}
            onChange={handleChalanInputChange}
            placeholder={t('permitEdit.fullNamePlaceholder')}
            variant="outlined"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('printCertificate.ownerCnicLabel')}
            name="owner_cnic"
            value={chalanData.owner_cnic || formData.owner_cnic}
            onChange={handleChalanInputChange}
            placeholder={t('permitEdit.cnicPlaceholder')}
            variant="outlined"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('printCertificate.ownerPhoneLabel')}
            name="owner_phone"
            value={chalanData.owner_phone || formData.owner_phone}
            onChange={handleChalanInputChange}
            placeholder={t('newPermit.phonePlaceholderShort')}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('newPermit.carNumberLabel')}
            name="car_number"
            value={chalanData.car_number || formData.vehicle_number}
            onChange={handleChalanInputChange}
            placeholder={t('newPermit.vehicleNumberPlaceholderShort')}
            variant="outlined"
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required sx={{ minWidth: '200px' }}>
            <InputLabel>{t('permitEdit.vehicleType')}</InputLabel>
            <Select
              name="vehicle_type"
              value={chalanData.vehicle_type || formData.vehicle_type_id}
              onChange={handleChalanInputChange}
              label={t('permitEdit.vehicleType')}
            >
              <MenuItem value="">{t('permitEdit.selectType')}</MenuItem>
              {vehicleTypes.map((type) => (
                <MenuItem key={type.id} value={String(type.id)}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitModal.vehicleMakeField')}
            value={formData.vehicle_make}
            variant="outlined"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitModal.vehicleModelField')}
            value={formData.vehicle_model}
            variant="outlined"
            disabled
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t('permitModal.vehicleYearField')}
            value={formData.vehicle_year}
            variant="outlined"
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('newPermit.feeDescriptionLabel')}
            name="violation_description"
            multiline
            rows={3}
            value={chalanData.violation_description}
            onChange={handleChalanInputChange}
            placeholder={t('newPermit.feeDescPlaceholder')}
            variant="outlined"
            required
            sx={{ minWidth: '250px' }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label={t('newPermit.feesAmountLabel')}
            name="fees_amount"
            value={chalanData.fees_amount}
            onChange={handleChalanInputChange}
            placeholder={t('newPermit.feesPlaceholder')}
            variant="outlined"
            inputProps={{ step: '0.01', min: '0' }}
            disabled={autoCalcFee}
            helperText={
              feeLoading
                ? t('newPermit.loadingFeeHelper')
                : (() => {
                    const systemFee = vehicleFee?.base_fee ?? vehicleFee?.fee ?? vehicleFee?.amount;
                    return systemFee !== undefined && systemFee !== null && systemFee !== ''
                      ? t('newPermit.autoCalcFeeAmount', { amount: parseFloat(systemFee).toLocaleString('en-US') })
                      : t('newPermit.autoCalcFeeGeneric');
                  })()
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Checkbox
                checked={autoCalcFee}
                disabled
              />
            }
            label={t('newPermit.autoCalcCheckboxLabel')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('permitEdit.remarksLabel')}
            name="remarks"
            multiline
            rows={2}
            value={chalanData.remarks}
            onChange={handleChalanInputChange}
            placeholder={t('newPermit.additionalNotesPlaceholder')}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <input
            type="file"
            id="chalan-attachment"
            style={{ display: 'none' }}
            onChange={handleChalanAttachmentChange}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          />
          <label htmlFor="chalan-attachment" style={{ cursor: 'pointer', display: 'block' }}>
            <Paper
              sx={{
                p: 2,
                border: '2px dashed #90caf9',
                textAlign: 'center',
                backgroundColor: '#f5faff',
              }}
            >
              <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                {t('newPermit.attachChalanDocLabel')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#5f6368' }}>
                {t('newPermit.requiredUploadNote')}
              </Typography>
            </Paper>
          </label>
          {chalanAttachment && (
            <Paper
              sx={{
                mt: 1.5,
                p: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                borderLeft: '4px solid #4caf50',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  {chalanAttachment.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#558b2f' }}>
                  {(chalanAttachment.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={handleRemoveChalanAttachment}
              >
                {t('permitEdit.remove')}
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<PrintIcon />}
          onClick={printChalanPreview}
          sx={{
            textTransform: 'none',
            fontSize: '16px',
            px: 3,
            py: 1.2,
          }}
        >
          {t('newPermit.printChalanPreviewBtn')}
        </Button>
      </Box>

      <Paper
        sx={{
          p: 2,
          background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
          borderLeft: '4px solid #1976d2',
        }}
      >
        <Typography variant="body2" sx={{ color: '#0d47a1' }}>
          <strong>{t('newPermit.infoLabel')}</strong>{t('newPermit.chalanStepInfoNote')}
        </Typography>
      </Paper>
    </Box>
  );

  if (wizardConfigLoading) {
    return <PageLoader variant="form" rows={5} />;
  }

  return (
    <Box sx={{ p: 3, minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Submit loading overlay */}
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff', flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
          {t('newPermit.submittingPermitMsg')}
        </Typography>
      </Backdrop>

      {/* Header */}
      <Box
        sx={{
          mb: 4,
          pb: 3,
          borderBottom: '2px solid #e0e0e0',
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(21, 101, 192, 0.04) 100%)',
          borderRadius: '8px',
          p: 2.5,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {t('newPermit.pageTitle')}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '1rem' }}>
          {t('newPermit.pageSubtitle')}
        </Typography>

        {!renewalPermitId && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant={viewMode === 'drafts' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('drafts')}
              sx={{ minWidth: 140 }}
            >
              {t('newPermit.draftPermitTab')}
            </Button>
            <Button
              variant={viewMode === 'new' ? 'contained' : 'outlined'}
              onClick={handleStartNewPermit}
              sx={{ minWidth: 140 }}
            >
              {t('nav.newPermit')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CameraIcon />}
              onClick={handleScanVehicleClick}
            >
              {t('newPermit.scanVehicleBtn')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleBulkPermitClick}
              sx={{ ml: 'auto' }}
            >
              {t('newPermit.bulkPermitBtn')}
            </Button>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
            />
          </Box>
        )}
      </Box>

      {message && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Alert
            severity={message.type}
            sx={{ flex: 1, borderRadius: '8px' }}
            onClose={() => setMessage(null)}
          >
            {message.text}
          </Alert>
          {message.type === 'success' && createdChalanId && (
            <Button
              variant="contained"
              color="info"
              startIcon={<PrintIcon />}
              onClick={printChalan}
              sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
            >
              {t('newPermit.printChalanBtn')}
            </Button>
          )}
        </Box>
      )}

      {!renewalPermitId && viewMode === 'drafts' && (
        <Card sx={{ mb: 3, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
              color: 'white',
              padding: '16px 24px',
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t('newPermit.resumeSavedDraftsHeader')}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {loadingDrafts ? t('newPermit.loadingDraftsHeader') : t('newPermit.savedDraftsCount', { count: userDrafts.length })}
              </Typography>
            </Box>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {loadingDrafts ? (
              <Typography>{t('newPermit.loadingDraftsBody')}</Typography>
            ) : userDrafts.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {userDrafts.map((draft) => {
                  const savedStep = typeof draft.wizard_step === 'number' ? draft.wizard_step : 0;
                  const clampedStep = Math.min(Math.max(0, savedStep), Math.max(0, enabledSteps.length - 1));
                  const stepLabel = enabledSteps[clampedStep]?.label || t('permitDetails.stepNumber', { number: clampedStep + 1 });
                  const progressPct = enabledSteps.length > 0
                    ? Math.round(((clampedStep) / enabledSteps.length) * 100)
                    : 0;
                  return (
                    <Card
                      key={draft.id}
                      sx={{
                        p: 2,
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {draft.vehicle_number || t('newPermit.vehicleNumberNotEntered')}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {t('newPermit.ownerColonLabel', { name: draft.owner_name || t('newPermit.notEnteredFallback') })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                            {t('newPermit.lastModifiedColonLabel', { date: new Date(draft.last_modified).toLocaleDateString() })}
                          </Typography>
                          {/* Step progress */}
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 500 }}>
                              {t('newPermit.savedAtLabel', { label: stepLabel, step: clampedStep + 1, total: enabledSteps.length })}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              <Box sx={{ flex: 1, height: 4, bgcolor: '#e0e0e0', borderRadius: 2 }}>
                                <Box sx={{ width: `${progressPct}%`, height: '100%', bgcolor: '#1976d2', borderRadius: 2, minWidth: progressPct > 0 ? 4 : 0 }} />
                              </Box>
                              <Typography variant="caption" sx={{ color: '#999', whiteSpace: 'nowrap' }}>
                                {progressPct}%
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button variant="outlined" onClick={() => handleLoadDraft(draft)}>
                            {t('newPermit.resumeBtn')}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDraftToDelete(draft)}
                          >
                            {t('common.delete')}
                          </Button>
                        </Box>
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Typography>{t('newPermit.noSavedDrafts')}</Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Permit Form with Chalan Step */}
      {(renewalPermitId || viewMode === 'new') && (
        <Card sx={{ mb: 3, borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
          <Box sx={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e3f2fd 100%)', borderBottom: '1px solid #e0e0e0' }}>
            <Stepper activeStep={activeStep} sx={{ p: 3 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: '14px',
                        fontWeight: 500,
                      },
                      '& .MuiStepLabel-label.Mui-active': {
                        fontWeight: 600,
                        color: '#1976d2',
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 4, minHeight: '400px' }}>
            {renderStep()}
          </CardContent>

          {/* Navigation Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'space-between',
              p: 3,
              backgroundColor: '#f5f7fa',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<BackIcon className="rtl-flip-icon" />}
                onClick={handleBack}
                disabled={activeStep === 0 || loading}
                sx={{
                  borderColor: '#ccc',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#999',
                    backgroundColor: '#f9f9f9',
                  },
                }}
              >
                {t('common.back')}
              </Button>

              <Button
                variant="outlined"
                onClick={handleSaveDraft}
                disabled={loading || draftSaving}
                sx={{
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  '&:hover': {
                    borderColor: '#f57c00',
                    backgroundColor: '#fff3e0',
                  },
                }}
              >
                {draftSaving ? t('profile.saving') : t('newPermit.saveAsDraftBtn')}
              </Button>
            </Box>

            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                endIcon={<NextIcon className="rtl-flip-icon" />}
                onClick={handleNext}
                disabled={!validateStep(activeStep) || loading}
                sx={{
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                  paddingX: 4,
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.6)',
                  },
                  '&:disabled': {
                    boxShadow: 'none',
                  },
                }}
              >
                {t('common.next')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                startIcon={<SubmitIcon />}
                onClick={() => setConfirmDialogOpen(true)}
                disabled={!validateStep(activeStep) || loading}
                sx={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
                  paddingX: 4,
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(76, 175, 80, 0.6)',
                  },
                  '&:disabled': {
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? t('newPermit.submittingBtn') : t('newPermit.submitBtn')}
              </Button>
            )}
          </Box>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
            color: 'white',
            padding: '20px 24px',
          }}
        >
          <SubmitIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('newPermit.confirmPermitCreationTitle')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {t('newPermit.reviewBeforeSubmitting')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('newPermit.confirmCreateText')}
          </Typography>
          <Alert severity="info">{t('newPermit.allChangesSavedNotice')}</Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="success"
            disabled={loading}
          >
            {loading ? t('newPermit.creatingBtn') : t('newPermit.createPermitBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!draftToDelete} onClose={() => setDraftToDelete(null)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
            color: 'white',
            padding: '20px 24px',
          }}
        >
          <DeleteIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('newPermit.deleteDraftTitle')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {t('newPermit.cannotBeUndone')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          <Typography variant="body2">
            {t('newPermit.confirmDeleteDraftText', { vehicle: draftToDelete?.vehicle_number || t('newPermit.thisPermitFallback') })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setDraftToDelete(null)} variant="outlined" disabled={deletingDraft}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDeleteDraft}
            variant="contained"
            color="error"
            disabled={deletingDraft}
          >
            {deletingDraft ? t('newPermit.deletingBtn') : t('newPermit.deleteDraftTitle')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default NewPermit;
